import { describeError } from '@/lib/form/errorMessages';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Permission,
  catalogCabinetAttributes,
  type CatalogCabinetAttributes,
  type CatalogHardwareListItem,
  type CatalogItemDetails,
  type CreateCatalogItemRequest,
  type CreateDrawerRequest,
  type MaterialListItem,
  type PreviewCabinetRequest,
  type UpdateCatalogItemRequest,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Form, TextField, SelectField, FormActions, FormErrorBanner } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { useAuth } from '@/features/auth/useAuth';
import {
  useCatalogItem,
  useCreateCatalogItem,
  useDeleteCatalogItem,
  useUpdateCatalogItem,
} from '@/features/catalog/hooks';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { CatalogDangerZone } from '@/features/catalog/components/CatalogDangerZone';
import {
  EdgeAutosuggest,
  HardwareAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { BreakdownPanel } from '@/features/pricing/components/BreakdownPanel';
import { usePreviewCabinet } from '@/features/pricing/hooks';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { AddDrawerDialog } from '@/features/project-items/drawers/components/AddDrawerDialog';
import { drawerDraftDisplayNames } from '@/features/project-items/cabinets/components/AddCabinetDialog';
import { CabinetDoubledCheckboxes } from '@/features/project-items/shared/CabinetDoubledCheckboxes';
import { useDefaultMaterialId } from '@/features/project-items/shared/useDefaultMaterial';
import {
  dedupeHardwareItems,
  hardwareItemsWithReplacement,
  HardwareItemRow,
} from '@/features/project-items/shared/CabinetHardwareItemRow';
import { chooseEdgeForMaterial } from '@/features/project-items/shared/edgeAutoSelect';
import { edgesApi } from '@/features/catalog/components/admin/api';

const decimalRegex = /^\d+(\.\d+)?$/;
const decimalString = z.string().regex(decimalRegex);
const positiveDecimalString = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});
const positiveIntegerString = z
  .string()
  .regex(/^\d+$/, 'Expected positive integer')
  .refine((v) => parseInt(v, 10) > 0, { message: 'Must be greater than 0' });
const requiredCatalogId = z.string().min(1, 'Choose a value from the suggestions');
const HARDWARE_PREVIEW_LIMIT = 3;
const DRAWER_PREVIEW_LIMIT = 3;

const selectedHardwareSchema = z.object({
  hardwareCatalogId: requiredCatalogId,
  quantity: positiveIntegerString,
  lengthM: z
    .string()
    .regex(/^\d*(\.\d+)?$/)
    .default('0'),
  coefficient: positiveDecimalString,
  name: z.string().optional(),
  sku: z.string().nullable().optional(),
  manufacturer: z.string().nullable().optional(),
  pricePerPiece: decimalString.optional(),
  currency: z.string().optional(),
});

const formSchema = z.object({
  name: z.string().trim().min(1).max(200),
  sku: z.string().trim().max(50).optional(),
  widthMm: z.coerce.number().int().positive().max(100_000),
  heightMm: z.coerce.number().int().positive().max(100_000),
  depthMm: z.coerce.number().int().positive().max(100_000),
  materialId: requiredCatalogId,
  edgeId: z.string(),
  frontMaterialId: z.string(),
  frontEdgeId: z.string(),
  backMaterialId: z.string(),
  materialName: z.string().optional(),
  frontMaterialName: z.string().optional(),
  backMaterialName: z.string().optional(),
  edgeName: z.string().optional(),
  intermediarCount: z.coerce.number().int().min(0).max(10_000),
  politeCount: z.coerce.number().int().min(0).max(10_000),
  intermediarDoubled: z.boolean().default(false),
  carcassDoubled: z.boolean().default(false),
  carcassExteriorFront: z.boolean().default(false),
  politeDoubled: z.boolean().default(false),
  unitPriceNet: decimalString,
  description: z.string().trim().max(2000).optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']).default('AUTO'),
  hardwareCatalogId: z.string().optional(),
  hardwareItems: z.array(selectedHardwareSchema).default([]),
  joinTypeKind: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']),
});
type FormValues = z.infer<typeof formSchema>;
type DrawerDraft = {
  id: string;
  body: CreateDrawerRequest;
};

const JOIN_TYPE_LABELS: Record<FormValues['joinTypeKind'], string> = {
  EUROSCREW: 'Eurosurub',
  DEMONTABIL: 'Demontabil',
  CLAMEX: 'Clamex',
  MITER_45: '45°',
  NONE: 'None',
};

function defaults(defaultMaterialId?: string): FormValues {
  return {
    name: '',
    sku: '',
    widthMm: 600,
    heightMm: 720,
    depthMm: 560,
    materialId: defaultMaterialId ?? '',
    edgeId: '',
    frontMaterialId: '',
    frontEdgeId: '',
    backMaterialId: '',
    materialName: '',
    frontMaterialName: '',
    backMaterialName: '',
    edgeName: '',
    intermediarCount: 0,
    politeCount: 0,
    intermediarDoubled: false,
    carcassDoubled: false,
    carcassExteriorFront: false,
    politeDoubled: false,
    unitPriceNet: '0.00',
    description: '',
    pricingMode: 'AUTO',
    hardwareCatalogId: '',
    hardwareItems: [],
    joinTypeKind: 'EUROSCREW',
  };
}

function parseAttributes(item: CatalogItemDetails | undefined): CatalogCabinetAttributes {
  if (!item?.attributes) return {};
  const parsed = catalogCabinetAttributes.safeParse(item.attributes);
  return parsed.success ? parsed.data : {};
}

function integerFromDecimal(value: string | undefined, fallback = '1'): string {
  if (!value) return fallback;
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return String(Math.max(1, Math.trunc(parsed)));
}

function drawerDraftId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseDecimalValue(value: string | number | null | undefined): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value ?? '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrencyValue(value: number): string {
  return `${value.toFixed(2)} RON`;
}

function drawerLineTotalNet(body: CreateDrawerRequest): number {
  return (
    parseDecimalValue(body.unitPriceNet) *
    parseDecimalValue(body.quantity || '1') *
    parseDecimalValue(body.coefficient || '1')
  );
}

function drawerDraftsFromItem(item: CatalogItemDetails): DrawerDraft[] {
  return (parseAttributes(item).drawers ?? []).map((body) => ({
    id: drawerDraftId(),
    body,
  }));
}

function valuesFromItem(item: CatalogItemDetails): FormValues {
  const attrs = parseAttributes(item);
  return {
    ...defaults(),
    name: item.name,
    sku: item.sku ?? '',
    widthMm: item.defaultWidthMm ?? 600,
    heightMm: item.defaultHeightMm ?? 720,
    depthMm: item.defaultDepthMm ?? 560,
    materialId: attrs.materialId ?? '',
    edgeId: attrs.edgeId ?? '',
    frontMaterialId: attrs.frontMaterialId ?? '',
    frontEdgeId: attrs.frontMaterialId ? (attrs.frontEdgeId ?? '') : '',
    backMaterialId: attrs.backMaterialId ?? '',
    materialName: item.material ?? '',
    frontMaterialName: attrs.frontMaterial ?? '',
    backMaterialName: attrs.backMaterial ?? '',
    edgeName: attrs.edging ?? '',
    intermediarCount: Math.max(0, attrs.intermediarCount ?? 0),
    politeCount: Math.max(0, attrs.politeCount ?? 0),
    intermediarDoubled: attrs.intermediarDoubled ?? false,
    carcassDoubled: attrs.carcassDoubled ?? false,
    carcassExteriorFront: attrs.carcassExteriorFront ?? false,
    politeDoubled: attrs.politeDoubled ?? false,
    unitPriceNet: item.defaultCostNet ?? '0.00',
    description: item.description ?? '',
    pricingMode: 'AUTO',
    hardwareItems: (attrs.hardwareItems ?? []).map((item) => ({
      hardwareCatalogId: item.hardwareCatalogId,
      quantity: integerFromDecimal(item.quantity),
      lengthM: '0',
      coefficient: item.coefficient,
      name: item.name,
      sku: item.sku,
      manufacturer: item.manufacturer,
      pricePerPiece: item.pricePerPiece,
      currency: item.currency,
    })),
    joinTypeKind: attrs.joinTypeKind ?? 'EUROSCREW',
  };
}

/**
 * `sourceAttributes` carries forward the cut-list ("Pe piese") fields this page
 * has no editor for. Without it, opening a by-pieces blueprint here and saving
 * would silently rebuild `attributes` without them and turn it back into an
 * empty parametric cabinet.
 */
function buildBody(
  values: FormValues,
  mode: 'create' | 'edit',
  drawerDrafts: DrawerDraft[],
  sourceAttributes: CatalogCabinetAttributes = {},
): CreateCatalogItemRequest | UpdateCatalogItemRequest {
  const hardwareItems: CatalogCabinetAttributes['hardwareItems'] = dedupeHardwareItems(
    values.hardwareItems,
  ).map((item) => {
    const currency: 'RON' | 'EUR' | 'USD' | undefined =
      item.currency === 'RON' || item.currency === 'EUR' || item.currency === 'USD'
        ? item.currency
        : undefined;
    return {
      hardwareCatalogId: item.hardwareCatalogId,
      quantity: item.quantity,
      coefficient: item.coefficient,
      name: item.name,
      sku: item.sku,
      manufacturer: item.manufacturer,
      pricePerPiece: item.pricePerPiece,
      currency,
    };
  });
  const attributes: CatalogCabinetAttributes = {
    materialId: values.materialId || null,
    edgeId: values.edgeId || null,
    frontMaterialId: values.frontMaterialId || null,
    frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
    backMaterialId: values.backMaterialId || null,
    backEdgeId: null,
    backHasEdge: false,
    frontMaterial: values.frontMaterialName || undefined,
    backMaterial: values.backMaterialName || undefined,
    edging: values.edgeName || undefined,
    intermediarCount: values.intermediarCount,
    politeCount: values.politeCount,
    joinType: JOIN_TYPE_LABELS[values.joinTypeKind],
    intermediarDoubled: values.intermediarDoubled,
    carcassDoubled: values.carcassDoubled,
    carcassExteriorFront: values.carcassExteriorFront,
    politeDoubled: values.politeDoubled,
    pricingMode: 'AUTO',
    joinTypeKind: values.joinTypeKind,
    hardwareItems,
    drawers: drawerDrafts.map((draft) => draft.body),
    // Not editable on this page — preserved verbatim. See buildBody's docblock.
    constructionMode: sourceAttributes.constructionMode,
    pieces: sourceAttributes.pieces,
  };
  const body = {
    name: values.name,
    sku: values.sku || undefined,
    description: values.description || undefined,
    defaultWidthMm: values.widthMm,
    defaultHeightMm: values.heightMm,
    defaultDepthMm: values.depthMm,
    material: values.materialName || undefined,
    defaultCostNet: values.unitPriceNet,
    currency: 'RON' as const,
    attributes: attributes as Record<string, unknown>,
  };
  return mode === 'create' ? { type: 'CABINET' as const, ...body } : body;
}

interface Props {
  mode: 'create' | 'edit';
}

export function CatalogCabinetFormPage({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? params.id : undefined;
  const { user } = useAuth();
  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canCreate = can(Permission.CATALOG_CREATE);
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);
  const defaultMaterialId = useDefaultMaterialId();

  const itemQuery = useCatalogItem(id);
  const createMut = useCreateCatalogItem();
  const updateMut = useUpdateCatalogItem(id ?? '');
  const deleteMut = useDeleteCatalogItem(id ?? '');
  const [hardwareCandidate, setHardwareCandidate] = useState<CatalogHardwareListItem | null>(null);
  const [hardwarePickerKey, setHardwarePickerKey] = useState(0);
  const [showAllHardware, setShowAllHardware] = useState(false);
  const [showAllDrawers, setShowAllDrawers] = useState(false);
  const [drawerDrafts, setDrawerDrafts] = useState<DrawerDraft[]>([]);
  const [drawerDraftsDirty, setDrawerDraftsDirty] = useState(false);
  const [drawerDraftDialogOpen, setDrawerDraftDialogOpen] = useState(false);
  const [editingDrawerDraft, setEditingDrawerDraft] = useState<DrawerDraft | null>(null);
  const [selectedCarcassMaterial, setSelectedCarcassMaterial] = useState<MaterialListItem | null>(
    null,
  );
  const [selectedFrontMaterial, setSelectedFrontMaterial] = useState<MaterialListItem | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults(defaultMaterialId),
  });
  const hardwareFieldArray = useFieldArray({
    control: form.control,
    name: 'hardwareItems',
  });

  useEffect(() => {
    if (mode !== 'edit' || !itemQuery.data) return;
    form.reset(valuesFromItem(itemQuery.data));
    setDrawerDrafts(drawerDraftsFromItem(itemQuery.data));
    setDrawerDraftsDirty(false);
    setHardwarePickerKey((key) => key + 1);
    setShowAllHardware(false);
    setShowAllDrawers(false);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
  }, [form, itemQuery.data, mode]);

  // Fill default material once it resolves (async), only for new items.
  useEffect(() => {
    if (mode !== 'create' || !defaultMaterialId) return;
    const current = form.getValues('materialId');
    if (!current) {
      form.setValue('materialId', defaultMaterialId);
    }
  }, [defaultMaterialId, form, mode]);

  const watchedRaw = form.watch();

  // ---- Edge auto-select for carcass + front materials ----------------------
  const carcassEdgeSearch = selectedCarcassMaterial?.manufacturer?.trim() ?? '';
  const frontEdgeSearch = selectedFrontMaterial?.manufacturer?.trim() ?? '';
  const carcassEdgesQuery = useQuery({
    queryKey: ['catalog-edges', 'auto-select', carcassEdgeSearch],
    queryFn: () =>
      edgesApi
        .list({
          page: 1,
          pageSize: 100,
          active: true,
          includeDeleted: false,
          search: carcassEdgeSearch,
        })
        .then((res) => res.items),
    enabled:
      Boolean(selectedCarcassMaterial) &&
      selectedCarcassMaterial?.category !== 'MDF_VOPSIT' &&
      Boolean(carcassEdgeSearch),
    meta: { silent: true },
    staleTime: 60_000,
  });
  const frontEdgesQuery = useQuery({
    queryKey: ['catalog-edges', 'auto-select', frontEdgeSearch],
    queryFn: () =>
      edgesApi
        .list({
          page: 1,
          pageSize: 100,
          active: true,
          includeDeleted: false,
          search: frontEdgeSearch,
        })
        .then((res) => res.items),
    enabled:
      Boolean(selectedFrontMaterial) &&
      selectedFrontMaterial?.category !== 'MDF_VOPSIT' &&
      Boolean(frontEdgeSearch),
    meta: { silent: true },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!selectedCarcassMaterial) return;
    if (selectedCarcassMaterial.category === 'MDF_VOPSIT' || !carcassEdgeSearch) {
      form.setValue('edgeId', '', { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (!carcassEdgesQuery.data) return;
    const edge = chooseEdgeForMaterial(selectedCarcassMaterial, carcassEdgesQuery.data, {
      preferLargestProduct: watchedRaw.carcassDoubled,
    });
    form.setValue('edgeId', edge?.id ?? '', { shouldDirty: true, shouldValidate: true });
    if (edge) {
      form.setValue('edgeName', edge.name, { shouldDirty: true });
    }
  }, [
    carcassEdgeSearch,
    carcassEdgesQuery.data,
    form,
    selectedCarcassMaterial,
    watchedRaw.carcassDoubled,
  ]);

  useEffect(() => {
    if (!selectedFrontMaterial) return;
    if (selectedFrontMaterial.category === 'MDF_VOPSIT' || !frontEdgeSearch) {
      form.setValue('frontEdgeId', '', { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (!frontEdgesQuery.data) return;
    const edge = chooseEdgeForMaterial(selectedFrontMaterial, frontEdgesQuery.data);
    form.setValue('frontEdgeId', edge?.id ?? '', { shouldDirty: true, shouldValidate: true });
  }, [frontEdgeSearch, frontEdgesQuery.data, form, selectedFrontMaterial]);

  const watched = useDebouncedValue(watchedRaw, 300);
  const previewBody: PreviewCabinetRequest | null = useMemo(() => {
    const widthMm = Number(watched.widthMm);
    const heightMm = Number(watched.heightMm);
    const depthMm = Number(watched.depthMm);
    const shelvesCount = Number(watched.politeCount ?? 0);
    const dividersCount = Number(watched.intermediarCount ?? 0);
    if (
      !watched.materialId ||
      !watched.joinTypeKind ||
      !Number.isFinite(widthMm) ||
      widthMm <= 0 ||
      !Number.isFinite(heightMm) ||
      heightMm <= 0 ||
      !Number.isFinite(depthMm) ||
      depthMm <= 0 ||
      !Number.isFinite(shelvesCount) ||
      shelvesCount < 0 ||
      !Number.isFinite(dividersCount) ||
      dividersCount < 0
    ) {
      return null;
    }
    return {
      widthMm,
      heightMm,
      depthMm,
      carcassMaterialId: watched.materialId,
      carcassEdgeId: watched.edgeId || null,
      frontMaterialId: watched.frontMaterialId || null,
      frontEdgeId: watched.frontMaterialId ? watched.frontEdgeId || null : null,
      backMaterialId: watched.backMaterialId || null,
      backEdgeId: null,
      backHasEdge: false,
      carcassDoubled: watched.carcassDoubled ?? false,
      carcassExteriorFront: watched.carcassExteriorFront ?? false,
      shelvesCount,
      shelvesDoubled: watched.politeDoubled ?? false,
      dividersCount,
      dividersDoubled: watched.intermediarDoubled ?? false,
      hardware: (watched.hardwareItems ?? [])
        .filter((item) => item.name && item.pricePerPiece !== undefined)
        .map((item) => ({
          name: item.name ?? '',
          quantity: item.quantity || '1',
          lengthM: item.lengthM || '0',
          coefficient: item.coefficient || '1.00',
          unitPriceNet: item.pricePerPiece ?? '0',
        })),
      joinType: watched.joinTypeKind,
      quantity: '1',
      // A blueprint preview shows cost, not a quote: the markup is chosen when
      // the cabinet is actually placed in a project.
      carcassCoefficient: '1',
      frontCoefficient: '1',
      backCoefficient: '1',
    };
  }, [watched]);
  const preview = usePreviewCabinet(previewBody);

  useEffect(() => {
    if (preview.data && previewBody) {
      const subtotal = parseFloat(preview.data.breakdown.subtotalNet);
      if (Number.isFinite(subtotal) && subtotal >= 0) {
        form.setValue('unitPriceNet', subtotal.toFixed(2), { shouldDirty: false });
      }
    }
  }, [form, preview.data, previewBody]);

  const addSelectedHardware = () => {
    if (!hardwareCandidate) return;
    const currentItems = form.getValues('hardwareItems') ?? [];
    const existingIndex = currentItems.findIndex(
      (item) => item.hardwareCatalogId === hardwareCandidate.id,
    );

    if (existingIndex >= 0) {
      if (existingIndex > 0) hardwareFieldArray.move(existingIndex, 0);
    } else {
      hardwareFieldArray.prepend({
        hardwareCatalogId: hardwareCandidate.id,
        quantity: '1',
        lengthM: '0',
        coefficient: '1.00',
        name: hardwareCandidate.name,
        sku: hardwareCandidate.sku,
        manufacturer: hardwareCandidate.manufacturer,
        pricePerPiece: hardwareCandidate.pricePerPiece,
        currency: hardwareCandidate.currency,
      });
    }

    form.setValue('hardwareCatalogId', '', {
      shouldDirty: false,
      shouldValidate: false,
    });
    setHardwareCandidate(null);
    setHardwarePickerKey((key) => key + 1);
  };

  const replaceHardwareItem = (index: number, item: CatalogHardwareListItem) => {
    const next = hardwareItemsWithReplacement(form.getValues('hardwareItems') ?? [], index, item);
    if (next) hardwareFieldArray.replace(next);
  };

  const closeDrawerDraftDialog = () => {
    setDrawerDraftDialogOpen(false);
    setEditingDrawerDraft(null);
  };

  const saveDrawerDraft = (body: CreateDrawerRequest) => {
    // Only on an add: editing a draft in place does not move it.
    if (!editingDrawerDraft) setShowAllDrawers(true);
    setDrawerDrafts((current) => {
      // Appended, not prepended: a drawer stack reads top-down, so the first
      // drawer added is the top one ("Sus" / "Sertar 1") and each new one goes
      // beneath it. The array order is the orderIndex the server will assign.
      if (!editingDrawerDraft) return [...current, { id: drawerDraftId(), body }];
      return current.map((draft) =>
        draft.id === editingDrawerDraft.id ? { ...draft, body } : draft,
      );
    });
    setDrawerDraftsDirty(true);
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (mode === 'create') {
        await createMut.mutateAsync(
          buildBody(
            values,
            mode,
            drawerDrafts,
            parseAttributes(itemQuery.data),
          ) as CreateCatalogItemRequest,
        );
        void navigate({ to: '/cabinets', search: {} });
        return;
      }
      if (!id) return;
      await updateMut.mutateAsync(
        buildBody(
          values,
          mode,
          drawerDrafts,
          parseAttributes(itemQuery.data),
        ) as UpdateCatalogItemRequest,
      );
    } catch {
      /* surfaced via mutation error */
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync();
      void navigate({ to: '/cabinets', search: {} });
    } catch {
      /* surfaced via delete error */
    }
  };

  const isReadOnly = (mode === 'edit' && !canUpdate) || (mode === 'create' && !canCreate);
  const submitting = mode === 'create' ? createMut.isPending : updateMut.isPending;
  const submitError = mode === 'create' ? createMut.error : updateMut.error;
  const visibleHardwareFields = showAllHardware
    ? hardwareFieldArray.fields
    : hardwareFieldArray.fields.slice(0, HARDWARE_PREVIEW_LIMIT);
  const hiddenHardwareCount = Math.max(
    0,
    hardwareFieldArray.fields.length - HARDWARE_PREVIEW_LIMIT,
  );
  const visibleDrawerDrafts = showAllDrawers
    ? drawerDrafts
    : drawerDrafts.slice(0, DRAWER_PREVIEW_LIMIT);
  // Drafts are not named until the server saves them; preview what is coming
  // rather than showing a bare "Sertar" for every row.
  const draftNames = drawerDraftDisplayNames(drawerDrafts);
  const hiddenDrawerDraftCount = Math.max(0, drawerDrafts.length - DRAWER_PREVIEW_LIMIT);

  if (mode === 'edit' && itemQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (mode === 'edit' && itemQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {describeError(t, itemQuery.error, errorKeyFor)}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <Link
        to="/cabinets"
        search={{}}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> {t('cabinets.backToList')}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">
        {t(mode === 'create' ? 'cabinets.create.title' : 'cabinets.edit.title')}
      </h1>

      <Form
        form={form}
        onSubmit={onSubmit}
        className="space-y-4 rounded-md border border-border bg-card p-4"
      >
        <fieldset disabled={isReadOnly} className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem]">
            <TextField name="name" label={t('projectItems.cabinets.fields.name')} autoFocus />
            <TextField name="sku" label={t('projectItems.cabinets.fields.sku')} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <TextField
              name="heightMm"
              label={t('projectItems.cabinets.fields.heightMm')}
              {...positiveIntegerFieldProps}
            />
            <TextField
              name="widthMm"
              label={t('projectItems.cabinets.fields.widthMm')}
              {...positiveIntegerFieldProps}
            />
            <TextField
              name="depthMm"
              label={t('projectItems.cabinets.fields.depthMm')}
              {...positiveIntegerFieldProps}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <MaterialAutosuggest
              name="materialId"
              label={t('projectItems.cabinets.fields.materialId')}
              placeholder={t('pricing.pickMaterial')}
              usageScope="GENERAL"
              required
              onSelectedOption={(item) => {
                setSelectedCarcassMaterial(item);
                form.setValue('materialName', item?.name ?? '', { shouldDirty: true });
                form.setValue('edgeId', '', { shouldDirty: true, shouldValidate: true });
              }}
            />
            <MaterialAutosuggest
              name="frontMaterialId"
              label={t('projectItems.cabinets.fields.frontMaterialId')}
              placeholder={t('pricing.pickMaterial')}
              usageScope="GENERAL"
              emptyOptionLabel={t('projectItems.cabinets.noMaterial')}
              onSelectedOption={(item) => {
                setSelectedFrontMaterial(item);
                form.setValue('frontMaterialName', item?.name ?? '', { shouldDirty: true });
                form.setValue('frontEdgeId', '', { shouldDirty: true, shouldValidate: true });
              }}
            />
            <MaterialAutosuggest
              name="backMaterialId"
              label={t('projectItems.cabinets.fields.backMaterialId')}
              placeholder={t('pricing.pickMaterial')}
              usageScope="GENERAL"
              emptyOptionLabel={t('projectItems.cabinets.noMaterial')}
              onSelectedOption={(item) =>
                form.setValue('backMaterialName', item?.name ?? '', { shouldDirty: true })
              }
            />
          </div>

          <CabinetDoubledCheckboxes />

          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="intermediarCount"
              label={t('projectItems.cabinets.fields.intermediarCount')}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
            />
            <TextField
              name="politeCount"
              label={t('projectItems.cabinets.fields.politeCount')}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <SelectField
              name="joinTypeKind"
              label={t('projectItems.cabinets.fields.joinTypeKind')}
              options={[
                { value: 'EUROSCREW', label: 'Eurosurub' },
                { value: 'DEMONTABIL', label: 'Demontabil' },
                { value: 'CLAMEX', label: 'Clamex' },
                { value: 'MITER_45', label: '45°' },
                { value: 'NONE', label: 'None' },
              ]}
            />
            <EdgeAutosuggest
              name="edgeId"
              label={t('projectItems.cabinets.fields.edgeId')}
              placeholder={t('pricing.pickEdge')}
              emptyOptionLabel={t('pricing.noEdge')}
              onSelectedOption={(item) =>
                form.setValue('edgeName', item?.name ?? '', { shouldDirty: true })
              }
            />
            <EdgeAutosuggest
              name="frontEdgeId"
              label={t('projectItems.cabinets.fields.frontEdgeId')}
              placeholder={t('pricing.pickEdge')}
              emptyOptionLabel={t('pricing.noEdge')}
            />
          </div>

          <fieldset className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium text-foreground">
              {t('projectItems.cabinets.hardware.selected')}
            </legend>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <HardwareAutosuggest
                key={hardwarePickerKey}
                name="hardwareCatalogId"
                label={t('projectItems.cabinets.fields.hardwareCatalogId')}
                placeholder={t('pricing.pickHardware')}
                onSelectedOption={setHardwareCandidate}
              />
              <Button
                type="button"
                variant="outline"
                className="self-end"
                onClick={addSelectedHardware}
                disabled={!hardwareCandidate}
              >
                {t('projectItems.cabinets.hardware.add')}
              </Button>
            </div>
            {hardwareFieldArray.fields.length > 0 ? (
              <div className="space-y-2">
                {visibleHardwareFields.map((field, index) => (
                  <HardwareItemRow
                    key={field.id}
                    index={index}
                    hardware={watchedRaw.hardwareItems?.[index] ?? field}
                    onRemove={() => hardwareFieldArray.remove(index)}
                    onReplace={(item) => replaceHardwareItem(index, item)}
                  />
                ))}
                {hiddenHardwareCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowAllHardware((current) => !current)}
                  >
                    {showAllHardware ? 'View less' : `View more (${hiddenHardwareCount})`}
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('projectItems.cabinets.hardware.empty')}
              </p>
            )}
          </fieldset>

          <fieldset className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium text-foreground">Sertare</legend>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Adauga sertare in sablonul de catalog; acestea vor fi create cand corpul este
                folosit intr-un proiect.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingDrawerDraft(null);
                  setDrawerDraftDialogOpen(true);
                }}
              >
                Adauga sertar
              </Button>
            </div>
            {drawerDrafts.length > 0 ? (
              <div className="space-y-1">
                {visibleDrawerDrafts.map((draft) => {
                  const drawerTotalNet = drawerLineTotalNet(draft.body);
                  return (
                    <div
                      key={draft.id}
                      className="grid gap-1.5 rounded-md border border-border bg-card p-1.5 md:grid-cols-[minmax(0,1fr)_auto]"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">
                          {draftNames.get(draft.id)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {[draft.body.drawerModel, draft.body.drawerType, draft.body.slideModel]
                            .filter(Boolean)
                            .join(' / ')}
                          {draft.body.hBox ? ` · H-box ${draft.body.hBox}` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {draft.body.heightMm} x {draft.body.widthMm} x {draft.body.depthMm} mm ·
                          Bucati {draft.body.quantity} · Coef. {draft.body.coefficient}
                        </p>
                        <p className="text-xs font-medium text-foreground">
                          Pret sertar: {formatCurrencyValue(drawerTotalNet)}
                        </p>
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            setEditingDrawerDraft(draft);
                            setDrawerDraftDialogOpen(true);
                          }}
                        >
                          Editeaza
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            setDrawerDrafts((current) =>
                              current.filter((item) => item.id !== draft.id),
                            );
                            setDrawerDraftsDirty(true);
                          }}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {hiddenDrawerDraftCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowAllDrawers((current) => !current)}
                  >
                    {showAllDrawers ? 'View less' : `View more (${hiddenDrawerDraftCount})`}
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nu ai adaugat inca sertare.</p>
            )}
          </fieldset>

          <div className="space-y-3 rounded-md border border-border p-3">
            <BreakdownPanel
              breakdown={preview.data?.breakdown ?? null}
              loading={preview.isFetching}
              error={preview.error}
            />
          </div>

          <TextField name="description" label={t('projectItems.cabinets.fields.notes')} />
        </fieldset>

        <FormErrorBanner error={submitError} resolveKey={errorKeyFor} />

        {!isReadOnly && (
          <FormActions
            submitLabel={t(mode === 'create' ? 'cabinets.create.submit' : 'common.save')}
            isSubmitting={submitting}
            submitDisabled={!form.formState.isDirty && !drawerDraftsDirty && mode === 'edit'}
          >
            <Link to="/cabinets" search={{}}>
              <Button type="button" variant="ghost">
                {t('common.cancel')}
              </Button>
            </Link>
          </FormActions>
        )}
      </Form>

      {mode === 'edit' && canDelete && itemQuery.data && (
        <CatalogDangerZone
          itemName={itemQuery.data.name}
          pending={deleteMut.isPending}
          error={deleteMut.error}
          onConfirm={onDelete}
        />
      )}

      {(drawerDraftDialogOpen || editingDrawerDraft) && (
        <AddDrawerDialog
          projectId="catalog"
          draft={editingDrawerDraft?.body ?? null}
          open={drawerDraftDialogOpen || editingDrawerDraft !== null}
          onClose={closeDrawerDraftDialog}
          onSaveDraft={saveDrawerDraft}
        />
      )}
    </div>
  );
}
